import { FC, useEffect, useMemo, useRef, useState } from 'react';
import styles from './TripEmissionLog.module.scss';
import { DataComplianceLogDefaults } from '@/pages/MyClimate/TripEmissionLog/LogForm.tsx';
import moment from 'moment/moment';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import queryKeys from '@/constants/queryKeys.ts';
import {
	getSelectableUncompliantItineraries,
	getUncompliantItineraries,
	getUncompliantTripsNum,
	setItineraryCompliant,
	setSelectedItinerariesCompliant,
} from '@/api/dataCompliance.ts';
import { listAirports } from '@/api/search.ts';
import Pagination from '@/components/ui/Pagination/Pagination.tsx';
import { toast } from 'react-toastify';
import { useModalStore } from '@/store/useModalStore.ts';
import TravelLogType = App.Enums.TravelLogType;
import DataComplianceUserItineraryData = App.Data.DataComplianceUserItineraryData;
import DataComplianceUserItineraryFilterBy = App.Enums.DataComplianceUserItineraryFilterBy;
import DataComplianceUserItinerariesRequest = App.Data.Request.DataComplianceUserItinerariesRequest;
import { trackEvent } from '@/utils/productAnalyticsUtil.ts';
import { productAnalyticsEvents } from '@/constants/productAnalyticsEvents.ts';
import { actionNeededReportingPeriod, getDataCompliancePeriodOptions } from '@/constants/dataCompliance.ts';
import Co2Period = App.Enums.Co2Period;
import { getEmissionTargets } from '@/api/climatePro.ts';
import DataComplianceUserItineraryReferenceData = App.Data.DataComplianceUserItineraryReferenceData;
import DataComplianceMissingType = App.Enums.DataComplianceMissingType;
import ResolveSelectedTripsModal from '@/pages/MyClimate/TripEmissionLog/ResolveSelectedTripsModal.tsx';
import TripDetailsModal from '@/pages/MyClimate/TripEmissionLog/TripDetailsModal.tsx';
import ActionNeededTripsTable from '@/pages/MyClimate/TripEmissionLog/ActionNeededTripsTable.tsx';
import ActionNeededSelectionBar from '@/pages/MyClimate/TripEmissionLog/ActionNeededSelectionBar.tsx';
import ActionNeededTableControls from '@/pages/MyClimate/TripEmissionLog/ActionNeededTableControls.tsx';
import {
	getDestinationDisplayName,
	getItineraryRowKey,
	getItinerarySelectionKey,
	SelectableVisibleItinerary,
	TripSelectionScope,
} from './actionNeededUtils.ts';

const dataComplianceMissingTripModal = 'dataComplianceMissingTripModal';
const resolveSelectedTripsModal = 'resolveSelectedTripsModal';
const completionSpinnerDelayMs = 700;
const rowRemovalAnimationDurationMs = 680;
const stepExitAnimationDurationMs = 140;
const stepEnterAnimationDurationMs = 180;

type ModalStepAnimationState = 'idle' | 'exit' | 'hidden' | 'enter';

const wait = (durationMs: number) => new Promise(resolve => setTimeout(resolve, durationMs));

interface DataComplianceTravelerProps {
	timePeriod: Co2Period;
	setTimePeriod: (timePeriod: Co2Period) => void;
	onTravelLogHistoryAdded?: () => void;
}

const DataComplianceTraveler: FC<DataComplianceTravelerProps> = ({
	timePeriod,
	setTimePeriod,
	onTravelLogHistoryAdded,
}) => {
	const [pagination, setPagination] = useState({ page: 1, perPage: 6 });
	const [selectedItinerary, setSelectedItinerary] = useState<DataComplianceUserItineraryData>();
	const [selectedRelationshipFilter, setSelectedRelationshipFilter] =
		useState<DataComplianceUserItineraryFilterBy | null>(null);
	const [step, setStep] = useState(1);
	const [stepAnimationState, setStepAnimationState] = useState<ModalStepAnimationState>('idle');
	const [tripMode, setTripMode] = useState<TravelLogType>('flight');
	const [logFormDefaults, setLogFormDefaults] = useState<DataComplianceLogDefaults>({});
	const [sortTripStartDate, setSortTripStartDate] = useState('desc');
	const [fadingOutRowKey, setFadingOutRowKey] = useState<string | null>(null);
	const [completedRowKey, setCompletedRowKey] = useState<string | null>(null);
	const [completedTripAddedToHistory, setCompletedTripAddedToHistory] = useState(false);
	const [showSpinner, setShowSpinner] = useState(false);
	const [isPeriodChangePending, setIsPeriodChangePending] = useState(false);
	const [selectedItineraries, setSelectedItineraries] = useState<
		Map<string, DataComplianceUserItineraryReferenceData>
	>(new Map());
	const [tripSelectionScope, setTripSelectionScope] = useState<TripSelectionScope | null>(null);
	const [isSelectingRemainingTrips, setIsSelectingRemainingTrips] = useState(false);
	const [isRefreshingResolvedTrips, setIsRefreshingResolvedTrips] = useState(false);
	const [hasConfirmedMissingDetailsNotNeeded, setHasConfirmedMissingDetailsNotNeeded] = useState(false);
	const previousTimePeriodRef = useRef(timePeriod);
	const stepExitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const stepEnterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const stepEnterFrameRef = useRef<number | null>(null);
	const bookerTabRef = useRef<HTMLButtonElement>(null);
	const travelerTabRef = useRef<HTMLButtonElement>(null);
	const [relationshipIndicatorLeft, setRelationshipIndicatorLeft] = useState(0);
	const [relationshipIndicatorWidth, setRelationshipIndicatorWidth] = useState(0);
	const { openModal, closeModal } = useModalStore();
	const queryClient = useQueryClient();
	const selectedTripsMutation = useMutation({
		mutationFn: setSelectedItinerariesCompliant,
	});

	const dataComplianceItinerariesQuery = useQuery({
		queryKey: [
			queryKeys.dataUncompliantItineraries,
			timePeriod,
			pagination.page,
			sortTripStartDate,
			selectedRelationshipFilter,
		],
		queryFn: async () => {
			const params: DataComplianceUserItinerariesRequest = {
				period: timePeriod,
				page: pagination.page,
				perPage: pagination.perPage,
				tripStartDate: sortTripStartDate,
				filterBy: selectedRelationshipFilter,
			};
			return await getUncompliantItineraries(params);
		},
		placeholderData: prev => prev,
		enabled: selectedRelationshipFilter !== null,
	});
	const selectedComplianceGapType =
		tripSelectionScope && tripSelectionScope !== 'all' ? tripSelectionScope : undefined;
	const scopedSelectableItinerariesQuery = useQuery({
		queryKey: [
			queryKeys.dataComplianceSelectableItineraries,
			timePeriod,
			selectedRelationshipFilter,
			selectedComplianceGapType,
		],
		queryFn: () =>
			getSelectableUncompliantItineraries({
				period: timePeriod,
				filterBy: selectedRelationshipFilter!,
				complianceGapType: selectedComplianceGapType,
			}),
		enabled:
			selectedRelationshipFilter !== null &&
			selectedComplianceGapType !== undefined &&
			selectedItineraries.size > 0,
	});

	const dataComplianceItineraryTotalsQuery = useQuery({
		queryKey: [queryKeys.dataUncompliantItineraries, 'relationshipTotals', timePeriod],
		queryFn: async () => {
			const params: DataComplianceUserItinerariesRequest = {
				period: timePeriod,
				page: 1,
				perPage: 1,
				tripStartDate: 'desc',
				filterBy: null,
			};
			return await getUncompliantItineraries(params);
		},
		placeholderData: prev => prev,
	});
	const selectedPeriodHasNoTrips =
		dataComplianceItineraryTotalsQuery.isSuccess &&
		!dataComplianceItineraryTotalsQuery.isPlaceholderData &&
		(dataComplianceItineraryTotalsQuery.data?.totals.bookerItinerariesCount ?? 0) === 0 &&
		(dataComplianceItineraryTotalsQuery.data?.totals.travelerItinerariesCount ?? 0) === 0;
	const currentReportingPeriodTripsQuery = useQuery({
		queryKey: [queryKeys.dataComplianceNumOfTrips, actionNeededReportingPeriod],
		queryFn: () => getUncompliantTripsNum({ period: actionNeededReportingPeriod }),
		enabled: timePeriod !== actionNeededReportingPeriod && selectedPeriodHasNoTrips,
	});
	const emissionTargetsQuery = useQuery({
		queryKey: [queryKeys.getEmissionTargets],
		queryFn: getEmissionTargets,
	});
	const currentEmissionTarget = useMemo(
		() => emissionTargetsQuery.data?.find(item => item.current),
		[emissionTargetsQuery.data],
	);
	const dataCompliancePeriodOptions = useMemo(
		() => getDataCompliancePeriodOptions(currentEmissionTarget),
		[currentEmissionTarget],
	);
	const trackActionNeededInteraction = (control: string) => {
		trackEvent(productAnalyticsEvents.myClimate.tripLog.actionNeeded.interact, {
			deduplicationKey: `${selectedItinerary?.id ?? 'unknown'}.${control}`,
		});
	};

	const handlePagination = (num: number) => {
		setPagination({ page: num, perPage: 6 });
	};

	const clearStepTransitionTimers = () => {
		if (stepExitTimeoutRef.current) {
			clearTimeout(stepExitTimeoutRef.current);
			stepExitTimeoutRef.current = null;
		}

		if (stepEnterTimeoutRef.current) {
			clearTimeout(stepEnterTimeoutRef.current);
			stepEnterTimeoutRef.current = null;
		}

		if (stepEnterFrameRef.current) {
			cancelAnimationFrame(stepEnterFrameRef.current);
			stepEnterFrameRef.current = null;
		}
	};

	const transitionModalStepContent = (updateContent: () => void) => {
		clearStepTransitionTimers();
		setStepAnimationState('exit');

		stepExitTimeoutRef.current = setTimeout(() => {
			setStepAnimationState('hidden');
			updateContent();

			stepEnterFrameRef.current = requestAnimationFrame(() => {
				setStepAnimationState('enter');

				stepEnterTimeoutRef.current = setTimeout(() => {
					setStepAnimationState('idle');
				}, stepEnterAnimationDurationMs);
			});
		}, stepExitAnimationDurationMs);
	};

	const changeCompletionStep = (nextStep: number) => {
		if (nextStep === step) {
			return;
		}

		transitionModalStepContent(() => setStep(nextStep));
	};

	const resetCompletionModal = () => {
		clearStepTransitionTimers();
		setSelectedItinerary(undefined);
		setStep(1);
		setStepAnimationState('idle');
		setLogFormDefaults({});
		setShowSpinner(false);
		setCompletedRowKey(null);
		setCompletedTripAddedToHistory(false);
	};

	const openCompletionModal = (item: DataComplianceUserItineraryData) => {
		clearStepTransitionTimers();
		setSelectedItinerary(item);
		setStep(1);
		setStepAnimationState('idle');
		setLogFormDefaults({});
		setShowSpinner(false);
		setCompletedRowKey(null);
		setCompletedTripAddedToHistory(false);
		openModal(dataComplianceMissingTripModal);
		trackEvent(productAnalyticsEvents.myClimate.tripLog.actionNeeded.interact, {
			deduplicationKey: `review.${item.id}.${item.travelerId}.${item.complianceGapType}`,
			postHogEventName: 'trip_log_action_needed_trip_reviewed',
			postHogProperties: {
				category: 'trip-log',
				itinerary_id: item.id,
				missing_details: item.complianceGapType,
			},
		});
	};

	const showAccommodationForm = () => {
		if (!selectedItinerary) {
			return;
		}

		setLogFormDefaults({
			...getTripDateDefaults(selectedItinerary),
			accommodationType: 'hotel',
		});
		trackActionNeededInteraction('choice.accommodation');
		setTripMode('accommodation');
		changeCompletionStep(2);
	};

	const getTripDateDefaults = (item: DataComplianceUserItineraryData): DataComplianceLogDefaults => ({
		calendarStartDate: moment(item.startAt),
		calendarEndDate: moment(item.endAt),
	});

	const showFlightForm = async () => {
		if (!selectedItinerary) {
			return;
		}

		const currentItinerary = selectedItinerary;
		const destinationName = getDestinationDisplayName(currentItinerary);
		let flightArrivalAirport: DataComplianceLogDefaults['flightArrivalAirport'];

		trackActionNeededInteraction('choice.flight');
		setLogFormDefaults(getTripDateDefaults(currentItinerary));
		setTripMode('flight');
		changeCompletionStep(2);

		try {
			const airportsResponse = await listAirports({ query: destinationName });
			const arrivalAirport = airportsResponse.airports
				.flatMap(group => group.items)
				.find(airport => /\([A-Z]{3}\)$/.test(airport.label));

			if (arrivalAirport) {
				flightArrivalAirport = {
					id: Number(arrivalAirport.value),
					name: arrivalAirport.label,
				};
			}
		} catch {
			flightArrivalAirport = undefined;
		}

		if (flightArrivalAirport) {
			setLogFormDefaults(prev => ({
				...prev,
				flightArrivalAirport,
			}));
		}
	};

	const showTransportForm = (groundTransportationType?: DataComplianceLogDefaults['groundTransportationType']) => {
		if (!selectedItinerary) {
			return;
		}

		const arrivalPlace = selectedItinerary.destinationPlace
			? {
					...selectedItinerary.destinationPlace,
					name: selectedItinerary.destinationPlace.name || selectedItinerary.city,
				}
			: undefined;
		const arrivalPlaceDisplayName = getDestinationDisplayName(selectedItinerary);

		setLogFormDefaults({
			...getTripDateDefaults(selectedItinerary),
			...(groundTransportationType && { groundTransportationType }),
			...(arrivalPlace && { arrivalPlace }),
			arrivalPlaceDisplayName,
		});
		trackActionNeededInteraction(`choice.transport.${groundTransportationType ?? 'road'}`);
		setTripMode('mileage');
		changeCompletionStep(2);
	};

	const invalidateDataComplianceQueries = () =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: [queryKeys.dataUncompliantItineraries] }),
			queryClient.invalidateQueries({ queryKey: [queryKeys.dataComplianceSelectableItineraries] }),
			queryClient.invalidateQueries({ queryKey: [queryKeys.dataComplianceNumOfTrips] }),
			queryClient.invalidateQueries({ queryKey: [queryKeys.dataComplianceSummary] }),
			queryClient.invalidateQueries({ queryKey: [queryKeys.dataCompliancePastTrips] }),
			queryClient.invalidateQueries({ queryKey: [queryKeys.dataComplianceSourceModelItineraries] }),
			queryClient.invalidateQueries({ queryKey: [queryKeys.dataComplianceTravelerItineraries] }),
		]);

	const removeCompletedItineraryRow = (rowKey: string) => {
		setFadingOutRowKey(rowKey);

		setTimeout(async () => {
			const currentData = dataComplianceItinerariesQuery.data;
			const totalItems = currentData?.pagination.total || 0;
			const newTotalItems = totalItems - 1;
			const newTotalPages = Math.max(1, Math.ceil(newTotalItems / pagination.perPage));
			const newPage = Math.min(pagination.page, newTotalPages);

			try {
				await invalidateDataComplianceQueries();
				setPagination({ page: newPage, perPage: 6 });
			} catch {
				toast.error('We couldn’t update your trips. Try again.');
			} finally {
				setFadingOutRowKey(null);
			}
		}, rowRemovalAnimationDurationMs);
	};

	const handleCompletionModalClose = () => {
		const completedKey = completedRowKey;
		const shouldAnimateHistoryBadge = completedTripAddedToHistory;

		resetCompletionModal();

		if (completedKey) {
			if (selectedItinerary?.travelerId != null) {
				handleTripSelectionChange(
					{ itineraryId: selectedItinerary.id, travelerId: selectedItinerary.travelerId },
					selectedItinerary.complianceGapType,
					false,
				);
			}
			removeCompletedItineraryRow(completedKey);
		}

		if (shouldAnimateHistoryBadge) {
			onTravelLogHistoryAdded?.();
		}
	};

	const markItineraryCompliant = async (
		item: DataComplianceUserItineraryData,
		shouldSetCompliant: boolean = true,
	) => {
		const travelerId = item.travelerId;

		let compliantPromise = Promise.resolve();

		if (shouldSetCompliant) {
			if (travelerId == null) {
				toast.error('We couldn’t save these details. Try again.');
				return;
			}

			compliantPromise = setItineraryCompliant(item.id, travelerId);
		}

		setShowSpinner(true);

		try {
			await Promise.all([compliantPromise, wait(completionSpinnerDelayMs)]);
			if (shouldSetCompliant) {
				trackEvent(productAnalyticsEvents.myClimate.tripLog.actionNeeded.submit, {
					deduplicationKey: `not-needed.${item.id}.${travelerId}.${item.complianceGapType}`,
					postHogEventName: 'trip_log_action_needed_submitted',
					postHogProperties: {
						category: 'trip-log',
						itinerary_id: item.id,
						missing_details: item.complianceGapType,
					},
				});
			}
			setCompletedTripAddedToHistory(!shouldSetCompliant);
			transitionModalStepContent(() => setCompletedRowKey(getItineraryRowKey(item)));
		} catch {
			setStep(1);
			setStepAnimationState('idle');
			toast.error('We couldn’t save these details. Try again.');
		} finally {
			setShowSpinner(false);
		}
	};

	const handleDetailsNotNeeded = () => {
		if (!selectedItinerary) {
			return;
		}

		trackActionNeededInteraction(`choice.not-needed.${selectedItinerary.complianceGapType}`);
		void markItineraryCompliant(selectedItinerary);
	};

	useEffect(() => {
		return () => {
			clearStepTransitionTimers();
		};
	}, []);

	const visibleItineraries = selectedRelationshipFilter ? dataComplianceItinerariesQuery.data?.data || [] : [];
	const selectableVisibleItineraries: SelectableVisibleItinerary[] = visibleItineraries.flatMap(item =>
		item.travelerId == null
			? []
			: [
					{
						reference: { itineraryId: item.id, travelerId: item.travelerId },
						complianceGapType: item.complianceGapType,
					},
				],
	);
	const visibleItineraryReferences = selectableVisibleItineraries.map(item => item.reference);
	const scopedVisibleItineraryReferences = selectableVisibleItineraries
		.filter(
			item =>
				tripSelectionScope === null ||
				tripSelectionScope === 'all' ||
				item.complianceGapType === tripSelectionScope,
		)
		.map(item => item.reference);
	const selectedTripsCount = selectedItineraries.size;
	const selectedTripsOnPageCount = visibleItineraryReferences.filter(reference =>
		selectedItineraries.has(getItinerarySelectionKey(reference)),
	).length;
	const allTripsOnPageSelected =
		visibleItineraryReferences.length > 0 && selectedTripsOnPageCount === visibleItineraryReferences.length;
	const someTripsOnPageSelected = selectedTripsOnPageCount > 0 && !allTripsOnPageSelected;
	const selectedScopedTripsOnPageCount = scopedVisibleItineraryReferences.filter(reference =>
		selectedItineraries.has(getItinerarySelectionKey(reference)),
	).length;
	const allScopedTripsOnPageSelected =
		scopedVisibleItineraryReferences.length > 0 &&
		selectedScopedTripsOnPageCount === scopedVisibleItineraryReferences.length;
	const activeViewTripsCount = dataComplianceItinerariesQuery.data?.pagination.total ?? 0;
	const activeSelectionScopeTripsCount = selectedComplianceGapType
		? scopedSelectableItinerariesQuery.data?.itineraries.length
		: activeViewTripsCount;
	const allTripsInSelectionScopeSelected =
		activeSelectionScopeTripsCount !== undefined &&
		activeSelectionScopeTripsCount > 0 &&
		selectedTripsCount >= activeSelectionScopeTripsCount;
	const remainingTripsCount =
		activeSelectionScopeTripsCount === undefined
			? undefined
			: Math.max(activeSelectionScopeTripsCount - selectedTripsCount, 0);
	const selectionScopeDescription =
		tripSelectionScope === 'missing-transport'
			? ' with missing transport'
			: tripSelectionScope === 'missing-hotel'
				? ' with missing accommodation'
				: '';
	const relationshipTotals = dataComplianceItineraryTotalsQuery.data?.totals;
	const currentReportingPeriodTripsCount = currentReportingPeriodTripsQuery.data?.count ?? 0;
	const shouldShowCurrentReportingPeriodTrips =
		timePeriod !== actionNeededReportingPeriod &&
		currentReportingPeriodTripsQuery.isSuccess &&
		currentReportingPeriodTripsCount > 0;
	const hasBookerTrips = (relationshipTotals?.bookerItinerariesCount ?? 0) > 0;
	const hasTravelerTrips = (relationshipTotals?.travelerItinerariesCount ?? 0) > 0;
	const relationshipTotalsReady =
		dataComplianceItineraryTotalsQuery.isSuccess && !dataComplianceItineraryTotalsQuery.isPlaceholderData;
	const relationshipTabsLoading = isPeriodChangePending || !relationshipTotalsReady;
	const isChangingRelationshipScope =
		dataComplianceItineraryTotalsQuery.isPlaceholderData ||
		(relationshipTotalsReady &&
			((selectedRelationshipFilter === null && (hasBookerTrips || hasTravelerTrips)) ||
				(selectedRelationshipFilter === 'booker' && !hasBookerTrips) ||
				(selectedRelationshipFilter === 'traveler' && !hasTravelerTrips)));
	const selectedPeriodDataReady =
		relationshipTotalsReady &&
		(selectedRelationshipFilter === null
			? !hasBookerTrips && !hasTravelerTrips
			: dataComplianceItinerariesQuery.isSuccess &&
				!dataComplianceItinerariesQuery.isPlaceholderData &&
				!dataComplianceItinerariesQuery.isFetching);
	const selectedPeriodQueriesFailed =
		(dataComplianceItineraryTotalsQuery.isError ||
			(selectedRelationshipFilter !== null && dataComplianceItinerariesQuery.isError)) &&
		!dataComplianceItinerariesQuery.isFetching &&
		!dataComplianceItineraryTotalsQuery.isFetching;
	const shouldShowTableSpinner =
		!fadingOutRowKey &&
		(isPeriodChangePending ||
			isRefreshingResolvedTrips ||
			(dataComplianceItineraryTotalsQuery.isFetching && !relationshipTotalsReady) ||
			(selectedRelationshipFilter !== null && dataComplianceItinerariesQuery.isPlaceholderData) ||
			(dataComplianceItinerariesQuery.isFetching && !visibleItineraries.length));

	useEffect(() => {
		if (previousTimePeriodRef.current === timePeriod) {
			return;
		}

		previousTimePeriodRef.current = timePeriod;
		setSelectedItineraries(new Map());
		setTripSelectionScope(null);
		setIsPeriodChangePending(true);
		setSelectedRelationshipFilter(null);
		setPagination(prev => ({ ...prev, page: 1 }));
	}, [timePeriod]);

	useEffect(() => {
		if (!relationshipTotalsReady) {
			return;
		}

		const nextRelationshipFilter =
			selectedRelationshipFilter === 'booker' && hasBookerTrips
				? 'booker'
				: selectedRelationshipFilter === 'traveler' && hasTravelerTrips
					? 'traveler'
					: hasBookerTrips
						? 'booker'
						: hasTravelerTrips
							? 'traveler'
							: null;

		if (nextRelationshipFilter !== selectedRelationshipFilter) {
			setSelectedRelationshipFilter(nextRelationshipFilter);
			setPagination(prev => ({ ...prev, page: 1 }));
		}
	}, [hasBookerTrips, hasTravelerTrips, relationshipTotalsReady, selectedRelationshipFilter]);

	useEffect(() => {
		if (isPeriodChangePending && (selectedPeriodDataReady || selectedPeriodQueriesFailed)) {
			setIsPeriodChangePending(false);
		}
	}, [isPeriodChangePending, selectedPeriodDataReady, selectedPeriodQueriesFailed]);

	useEffect(() => {
		if (!selectedRelationshipFilter) {
			return;
		}

		const activeButton = selectedRelationshipFilter === 'booker' ? bookerTabRef.current : travelerTabRef.current;
		const parentRect = activeButton?.parentElement?.getBoundingClientRect();
		const activeRect = activeButton?.getBoundingClientRect();

		if (parentRect && activeRect) {
			setRelationshipIndicatorLeft(activeRect.left - parentRect.left);
			setRelationshipIndicatorWidth(activeRect.width);
		}
	}, [selectedRelationshipFilter]);

	const clearSelectedTrips = () => {
		setSelectedItineraries(new Map());
		setTripSelectionScope(null);
		setHasConfirmedMissingDetailsNotNeeded(false);
	};

	const openResolveSelectedTripsModal = () => {
		setHasConfirmedMissingDetailsNotNeeded(false);
		openModal(resolveSelectedTripsModal);
	};

	const closeResolveSelectedTripsModal = () => {
		closeModal(() => setHasConfirmedMissingDetailsNotNeeded(false));
	};

	const handleTripSelectionChange = (
		reference: DataComplianceUserItineraryReferenceData,
		complianceGapType: DataComplianceMissingType,
		isSelected: boolean,
	) => {
		if (isSelected) {
			trackActionNeededInteraction(
				`selection.trip.${reference.itineraryId}.${reference.travelerId}.${complianceGapType}`,
			);
		}

		if (
			tripSelectionScope === 'all' ||
			(isSelected && tripSelectionScope !== null && tripSelectionScope !== complianceGapType)
		) {
			setTripSelectionScope(null);
		}

		setSelectedItineraries(currentSelection => {
			const nextSelection = new Map(currentSelection);
			const selectionKey = getItinerarySelectionKey(reference);

			if (isSelected) {
				nextSelection.set(selectionKey, reference);
			} else {
				nextSelection.delete(selectionKey);
			}

			return nextSelection;
		});
	};

	const handlePageSelectionChange = (isSelected: boolean) => {
		if (isSelected) {
			trackActionNeededInteraction(
				`selection.page.${selectedRelationshipFilter ?? 'unknown'}.${pagination.page}`,
			);
		}

		setTripSelectionScope(isSelected ? 'all' : null);
		setSelectedItineraries(currentSelection => {
			const nextSelection = new Map(currentSelection);

			visibleItineraryReferences.forEach(reference => {
				const selectionKey = getItinerarySelectionKey(reference);

				if (isSelected) {
					nextSelection.set(selectionKey, reference);
				} else {
					nextSelection.delete(selectionKey);
				}
			});

			return nextSelection;
		});
	};

	const handleSelectionScopeChange = (scope: TripSelectionScope) => {
		trackActionNeededInteraction(`selection.scope.${scope}`);

		const matchingReferences = selectableVisibleItineraries
			.filter(item => scope === 'all' || item.complianceGapType === scope)
			.map(item => item.reference);

		setTripSelectionScope(scope);
		setSelectedItineraries(
			new Map(matchingReferences.map(reference => [getItinerarySelectionKey(reference), reference])),
		);
	};

	const handleSelectRemainingTrips = async () => {
		if (!selectedRelationshipFilter) {
			return;
		}

		trackActionNeededInteraction(`selection.remaining.${selectedComplianceGapType ?? 'all'}`);
		setIsSelectingRemainingTrips(true);

		try {
			const response = await queryClient.fetchQuery({
				queryKey: [
					queryKeys.dataComplianceSelectableItineraries,
					timePeriod,
					selectedRelationshipFilter,
					selectedComplianceGapType,
				],
				queryFn: () =>
					getSelectableUncompliantItineraries({
						period: timePeriod,
						filterBy: selectedRelationshipFilter,
						complianceGapType: selectedComplianceGapType,
					}),
				staleTime: 0,
			});

			setSelectedItineraries(
				new Map(response.itineraries.map(reference => [getItinerarySelectionKey(reference), reference])),
			);
		} catch {
			toast.error('We couldn’t select the remaining trips. Try again.');
		} finally {
			setIsSelectingRemainingTrips(false);
		}
	};

	const handleResolveSelectedTrips = async () => {
		const selectedReferences = Array.from(selectedItineraries.values());
		const requestedCount = selectedReferences.length;

		if (!requestedCount || !hasConfirmedMissingDetailsNotNeeded) {
			return;
		}

		try {
			await selectedTripsMutation.mutateAsync({ itineraries: selectedReferences });
		} catch {
			toast.error('We couldn’t update the selected trips. Try again.');
			return;
		}

		setPagination(currentPagination => ({ ...currentPagination, page: 1 }));
		setIsRefreshingResolvedTrips(true);
		closeModal(clearSelectedTrips);

		toast.success(`${requestedCount} ${requestedCount === 1 ? 'trip' : 'trips'} resolved.`);

		trackEvent(productAnalyticsEvents.myClimate.tripLog.actionNeeded.submit, {
			deduplicationKey: `resolve-without-details.selected.${requestedCount}`,
			postHogEventName: 'trip_log_action_needed_selected_submitted',
			postHogProperties: {
				category: 'trip-log',
				resolution: 'without-details',
				selected_count: requestedCount,
			},
		});

		try {
			await invalidateDataComplianceQueries();
		} catch {
			toast.error('Your changes were saved, but the list couldn’t refresh. Reload the page to update it.');
		} finally {
			setIsRefreshingResolvedTrips(false);
		}
	};

	const handleRelationshipFilterChange = (filterBy: DataComplianceUserItineraryFilterBy) => {
		clearSelectedTrips();
		setSelectedRelationshipFilter(filterBy);
		setPagination(prev => ({ ...prev, page: 1 }));
	};

	const handleResolveConfirmationChange = (isConfirmed: boolean) => {
		if (isConfirmed) {
			trackActionNeededInteraction('selection.confirm-without-details');
		}

		setHasConfirmedMissingDetailsNotNeeded(isConfirmed);
	};

	const handleRetryTrips = () => {
		void dataComplianceItineraryTotalsQuery.refetch();
		if (selectedRelationshipFilter) {
			void dataComplianceItinerariesQuery.refetch();
		}
	};

	const handleTimePeriodChange = (value: string | number | undefined) => {
		if (!value) {
			return;
		}

		const period = value as Co2Period;
		previousTimePeriodRef.current = period;
		clearSelectedTrips();
		setIsPeriodChangePending(true);
		setTimePeriod(period);
		setSelectedRelationshipFilter(null);
		setPagination(prev => ({ ...prev, page: 1 }));
		trackEvent(productAnalyticsEvents.myClimate.tripLog.actionNeeded.interact, {
			deduplicationKey: `period.${period}`,
			postHogEventName: 'trip_log_action_needed_period_changed',
			postHogProperties: {
				category: 'trip-log',
				period,
			},
		});
	};

	return (
		<div className={styles.dataComplianceTravelerWrapper}>
			{dataComplianceItineraryTotalsQuery.isError || dataComplianceItinerariesQuery.isError ? (
				<div className={styles.errorMsg}>
					<p>We couldn’t load your trips. Try again in a moment.</p>
					<button type="button" onClick={handleRetryTrips}>
						Try again
					</button>
				</div>
			) : (
				<div className={styles.tableWrapper}>
					<ActionNeededTableControls
						selectedRelationship={selectedRelationshipFilter}
						period={timePeriod}
						periodOptions={dataCompliancePeriodOptions}
						bookerTabRef={bookerTabRef}
						travelerTabRef={travelerTabRef}
						hasBookerTrips={hasBookerTrips}
						hasTravelerTrips={hasTravelerTrips}
						relationshipTotalsReady={relationshipTotalsReady}
						relationshipTabsLoading={relationshipTabsLoading}
						indicatorLeft={relationshipIndicatorLeft}
						indicatorWidth={relationshipIndicatorWidth}
						onRelationshipChange={handleRelationshipFilterChange}
						onPeriodChange={handleTimePeriodChange}
					/>
					<ActionNeededSelectionBar
						selectedTripsCount={selectedTripsCount}
						selectedTripsOnPageCount={selectedScopedTripsOnPageCount}
						allTripsSelected={allTripsInSelectionScopeSelected}
						allTripsOnPageSelected={allScopedTripsOnPageSelected}
						selectionScopeDescription={selectionScopeDescription}
						hasSelectedComplianceGapType={selectedComplianceGapType !== undefined}
						isCheckingRemainingTrips={scopedSelectableItinerariesQuery.isPending}
						hasRemainingTripsError={scopedSelectableItinerariesQuery.isError}
						remainingTripsCount={remainingTripsCount}
						isSelectingRemainingTrips={isSelectingRemainingTrips}
						isResolvingTrips={selectedTripsMutation.isPending}
						onRetryRemainingTrips={() => void scopedSelectableItinerariesQuery.refetch()}
						onSelectRemainingTrips={() => void handleSelectRemainingTrips()}
						onDeselectAll={clearSelectedTrips}
						onResolve={openResolveSelectedTripsModal}
					/>
					<ActionNeededTripsTable
						itineraries={visibleItineraries}
						selectableItineraries={selectableVisibleItineraries}
						selectedItineraries={selectedItineraries}
						tripSelectionScope={tripSelectionScope}
						selectedTripsCount={selectedTripsCount}
						allTripsOnPageSelected={allTripsOnPageSelected}
						someTripsOnPageSelected={someTripsOnPageSelected}
						selectedPeriodDataReady={selectedPeriodDataReady}
						isChangingRelationshipScope={isChangingRelationshipScope}
						fadingOutRowKey={fadingOutRowKey}
						shouldShowSpinner={shouldShowTableSpinner}
						selectionDisabled={
							shouldShowTableSpinner || isSelectingRemainingTrips || selectedTripsMutation.isPending
						}
						shouldShowCurrentReportingPeriodTrips={shouldShowCurrentReportingPeriodTrips}
						currentReportingPeriodTripsCount={currentReportingPeriodTripsCount}
						sortTripStartDate={sortTripStartDate}
						onPageSelectionChange={handlePageSelectionChange}
						onSelectionScopeChange={handleSelectionScopeChange}
						onTripSelectionChange={handleTripSelectionChange}
						onToggleSort={() => setSortTripStartDate(current => (current === 'asc' ? 'desc' : 'asc'))}
						onOpenTrip={openCompletionModal}
						onViewCurrentReportingPeriodTrips={() => handleTimePeriodChange(actionNeededReportingPeriod)}
					/>
				</div>
			)}
			{!!visibleItineraries.length &&
				!!dataComplianceItinerariesQuery.data?.pagination &&
				dataComplianceItinerariesQuery.data?.pagination.totalPages > 1 && (
					<Pagination
						records={dataComplianceItinerariesQuery.data?.pagination.total}
						current={dataComplianceItinerariesQuery.data?.pagination.currentPage || 1}
						onChange={handlePagination}
						delta={2}
						fixed={1}
						limit={pagination.perPage}
					/>
				)}
			<ResolveSelectedTripsModal
				name={resolveSelectedTripsModal}
				selectedTripsCount={selectedTripsCount}
				isPending={selectedTripsMutation.isPending}
				isRefreshing={isRefreshingResolvedTrips}
				isConfirmed={hasConfirmedMissingDetailsNotNeeded}
				onConfirmationChange={handleResolveConfirmationChange}
				onClose={closeResolveSelectedTripsModal}
				onResolve={() => void handleResolveSelectedTrips()}
			/>
			<TripDetailsModal
				name={dataComplianceMissingTripModal}
				selectedItinerary={selectedItinerary}
				step={step}
				tripMode={tripMode}
				logFormDefaults={logFormDefaults}
				completedRowKey={completedRowKey}
				showSpinner={showSpinner}
				animationState={stepAnimationState}
				onClose={handleCompletionModalClose}
				onCloseButtonClick={() => closeModal(handleCompletionModalClose)}
				onShowAccommodationForm={showAccommodationForm}
				onShowFlightForm={showFlightForm}
				onShowTransportForm={showTransportForm}
				onDetailsNotNeeded={handleDetailsNotNeeded}
				onDetailsSubmitted={() => {
					if (selectedItinerary) {
						void markItineraryCompliant(selectedItinerary, false);
					}
				}}
				onSubmittingChange={setShowSpinner}
				onStepBack={() => changeCompletionStep(1)}
			/>
		</div>
	);
};

export default DataComplianceTraveler;
